import { apiFetch } from '../api/http';
import { toAppError } from '../utils/appErrors';

/* ---------- Types ---------- */

export type DeepResearchRequest = {
  job_id: string;
  venue_query: string;
  deep_research_requested_at?: string | null;
};

/* ---------- Query key ---------- */

export const DEEP_REQUESTS_QUERY_KEY = ['ai', 'deep-requests'] as const;
export const DEEP_REQUESTS_POLL_INTERVAL_MS = 10_000;
export const DEEP_REQUESTS_ERROR_BACKOFF_MS = 60_000;

export function deepRequestsRefetchInterval(query: { state?: { status?: string } }): number | false {
  if (typeof document !== 'undefined' && document.hidden) return false;
  return query.state?.status === 'error'
    ? DEEP_REQUESTS_ERROR_BACKOFF_MS
    : DEEP_REQUESTS_POLL_INTERVAL_MS;
}

/* ---------- Fetch ---------- */

export async function fetchDeepRequests(
  _accessToken: string | undefined | null,
): Promise<DeepResearchRequest[]> {
  const response = await apiFetch('/ai/deep-requests');
  if (!response.ok) {
    throw toAppError(
      { status: response.status },
      'Deep research requests could not be loaded.',
      'deep_research.requests',
    );
  }
  try {
    const payload = await response.json();
    return (payload?.items ?? []).slice(0, 10);
  } catch (error) {
    throw toAppError(
      error,
      'The deep research service returned an invalid response.',
      'deep_research.requests_response',
    );
  }
}

/* ---------- Approve / Deny ---------- */

export async function approveDeepResearch(jobId: string): Promise<{ ok: boolean }> {
  const response = await apiFetch(`/ai/venue-jobs/${jobId}/approve-deep`, {
    method: 'POST',
  });
  if (!response.ok) {
    throw toAppError(
      { status: response.status },
      'Deep research approval could not be completed.',
      'deep_research.approve',
      'mutation',
    );
  }
  return { ok: true };
}

export async function denyDeepResearch(jobId: string): Promise<{ ok: boolean }> {
  const response = await apiFetch(`/ai/venue-jobs/${jobId}/deny-deep`, {
    method: 'POST',
  });
  if (!response.ok) {
    throw toAppError(
      { status: response.status },
      'Deep research denial could not be completed.',
      'deep_research.deny',
      'mutation',
    );
  }
  return { ok: true };
}
